import React, { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { StatusCodes } from 'http-status-codes';
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import { AuthPageWrapper } from 'features/authentication/auth-page-wrapper/auth-page-wrapper';
import { Button, GoogleButton, Heading, Link } from '@placer-ui/components';
import { useAuthenticateWithGoogle } from 'features/authentication/hooks/use-authenticate-with-google';
import { useQuery, useRouteParam } from 'hooks/use-query/use-query';
import { SsoLink } from 'features/authentication/sso/components/sso-link/sso-link';
import { OrDivider } from 'features/authentication/components/or-divider/or-divider';
import { ControlledInputField } from 'components/controlled-input-field';
import { emailRules } from 'features/authentication/signup/validations/validations';
import { useForm } from 'react-hook-form';
import { PasswordFieldController } from 'features/authentication/signup/components/password-field-controller/password-field-controller';
import { loginPasswordRules } from 'features/authentication/form-validations/form-validations';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    BACKEND_RECAPTCHA_ERROR_CODE,
    EMAIL_VALIDATION_REQUIRED_FOR_LOGIN,
    forgotPasswordLinkClickSplunkData,
    GOOGLE_RECAPTCH_VERSION,
    GOOGLE_RECAPTCHA_SITE_KEY,
    LOGIN_WITH_UNAVAILABLE_EMAIL,
    loginBaseSplunkData,
} from 'features/authentication/utils/constants';
import { useAuthActions } from 'store/auth/use-auth-actions';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { LoginParameters } from 'store/auth/actions/auth-actions/auth-action-types';
import { AuthError } from 'features/authentication/utils/auth-error';
import { reportException } from 'core/exceptions';
import { useHasAnonymousUserPermissions } from 'store/selectors/hooks/anonymous-user-permissions';
import { goToForgotPasswordPage, goToSignUp } from 'router/go-to';
import { getUrlToForceSso } from 'features/authentication/utils/sso/sso';
import { useHistory } from 'react-router-dom';
import { sendHubspotForm } from 'features/authentication/login-page/utils/send-hubspot-form';
import { ErrorBanner } from 'features/authentication/login-page/components/error-banner';
import { shouldRenderMobileApp } from 'utils/detect-environment';
import { WebviewLogin } from 'features/authentication/components/webview-login/webview-login';
import styles from './login-page.module.scss';

export const LoginPage = () => {
    const hasSentryPermission = useHasAnonymousUserPermissions('enable_sentry');
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>('');
    const [authError, setAuthError] = useState<AuthError | undefined>();
    const [showRecaptcha, setShowRecaptcha] = useState(false);
    const [wasPageVisited, setWasPageVisited] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const sendEvent = useSplunkCallback(loginBaseSplunkData);
    const sendSplunkClickEvent = useSplunkCallback(forgotPasswordLinkClickSplunkData);
    const history = useHistory();
    const isMobile = shouldRenderMobileApp();
    const nextUrl = useRouteParam('next_url');
    const urlUtmParams = {
        utm_source: useQuery('utm_source'),
        utm_medium: useQuery('utm_medium'),
        utm_campaign: useQuery('utm_campaign'),
    };
    const { login } = useAuthActions();
    const { loginViaGoogle } = useAuthenticateWithGoogle({
        authMode: 'signin',
        urlUtmParams,
        setServerError: setAuthError,
    });
    const { control, handleSubmit } = useForm<LoginParameters>({
        defaultValues: {
            username: '',
            password: '',
            recaptcha_response: recaptchaToken || '',
            recaptcha_version: GOOGLE_RECAPTCH_VERSION,
        },
        mode: 'onChange',
    });

    useEffect(() => {
        if (authError?.status === BACKEND_RECAPTCHA_ERROR_CODE) {
            recaptchaRef.current?.reset();
            setAuthError(undefined);
        }
    }, [authError?.status]);

    useEffect(() => {
        setAuthError(undefined);
        if (!wasPageVisited) {
            sendEvent({
                action: 'signin-page-viewed',
            });
            setWasPageVisited(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendEvent]);

    useEffect(() => {
        if (hasSentryPermission && authError?.shouldReportToSentry()) {
            reportException(authError, {
                payload: { page: 'login' },
            });
        }
    }, [authError, hasSentryPermission]);

    const onRecaptchaChange = (token: string | null) => {
        setRecaptchaToken(token);
    };

    const onRecaptchaExpired = () => {
        setRecaptchaToken(null);
    };

    const onSubmit = async (inputValues: LoginParameters) => {
        setAuthError(undefined);
        sendEvent({
            action: 'signin-page-login-click',
        });

        if (showRecaptcha && !recaptchaToken) return;

        try {
            sendHubspotForm(inputValues.username);
            const loginParameters = {
                ...inputValues,
                recaptcha_response: recaptchaToken || '',
                recaptcha_version: GOOGLE_RECAPTCH_VERSION,
                next_url: nextUrl ? decodeURIComponent(nextUrl) : undefined,
            };

            await login(loginParameters);
            sendEvent({
                action: 'signin-success',
                method: 'email',
            });
        } catch (error) {
            const authError = AuthError.fromUnknownError(error);
            if (authError.shouldForceSso) {
                const ssoPath = getUrlToForceSso(
                    inputValues.username,
                    authError.details?.account,
                    nextUrl,
                );
                history.push(ssoPath);
            } else if (authError.status === BACKEND_RECAPTCHA_ERROR_CODE && !showRecaptcha) {
                sendEvent({
                    action: 'recaptcha-show',
                });
                setShowRecaptcha(true);
            } else if (authError.status === StatusCodes.UNAUTHORIZED) {
                if (authError.error_code === EMAIL_VALIDATION_REQUIRED_FOR_LOGIN) {
                    history.push('mfa/email?email=' + encodeURIComponent(authError.details.email));
                } else if (authError.error_code === LOGIN_WITH_UNAVAILABLE_EMAIL) {
                    history.push(
                        'mfa/email-bounced?email=' + encodeURIComponent(authError.details.email),
                    );
                } else {
                    setAuthError(authError);
                }
            } else {
                setAuthError(authError);
            }
        }
    };

    const handleForgotPasswordClick = () => {
        sendSplunkClickEvent();
        goToForgotPasswordPage({
            nextUrl,
        });
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.charCode === 13) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    const loginErrorCallback = useCallback((error) => {
        console.error('Signin Button error', error);
    }, []);

    const googleLogin = useGoogleLogin({
        onSuccess: (tokenResponse: TokenResponse) => {
            loginViaGoogle(tokenResponse.access_token);
        },
        onError: loginErrorCallback,
    });

    const errorCode = authError?.status;

    if (window.placerMobile) return <WebviewLogin />;

    return (
        <AuthPageWrapper>
            {errorCode && <ErrorBanner errorCode={errorCode} />}
            <div className={styles.loginPageContainer} data-testId={'login-page-container'}>
                <Heading variant={isMobile ? 'h4' : 'h3'} testId="login-page-login-header">
                    Log in to your account
                </Heading>
                <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
                    <GoogleButton fullWidth label="Sign in with Google" onClick={googleLogin} />
                    <SsoLink sourcePage="login" />
                    <OrDivider />
                    <ControlledInputField<LoginParameters>
                        name="username"
                        placeholder="name@company.com"
                        control={control}
                        rules={emailRules}
                        onKeyPress={handleKeyPress}
                        autoComplete="username"
                        testId={'email-input-field'}
                        label={'Email Address'}
                        required
                        asterix
                    />
                    <div className={styles.passwordFieldContainer}>
                        <PasswordFieldController<LoginParameters>
                            name="password"
                            control={control}
                            required
                            label={'Password'}
                            asterix
                            testId="login-password-input"
                            placeholder="Password"
                            onKeyPress={handleKeyPress}
                            rules={loginPasswordRules}
                            autoComplete="current-password"
                        />
                        <div className={styles.forgotPasswordLink}>
                            <Link
                                size="large"
                                weight="semi-bold"
                                onClick={handleForgotPasswordClick}
                                hideUnderline={false}
                                testId={'forgot-password-link-component'}
                            >
                                Forgot Password?
                            </Link>
                        </div>
                    </div>
                    {showRecaptcha && (
                        <ReCAPTCHA
                            sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                            onChange={onRecaptchaChange}
                            onExpired={onRecaptchaExpired}
                            ref={recaptchaRef}
                        />
                    )}
                    <Button testId="login-page-login-button" fullWidth type="submit">
                        Login
                    </Button>
                </form>
                <div className={styles.signupFooter}>
                    <span>Don't have an account?</span>
                    <Link
                        onClick={() =>
                            goToSignUp({
                                nextUrl,
                            })
                        }
                        hideUnderline={false}
                        testId={'signup-link-component'}
                    >
                        Sign Up
                    </Link>
                </div>
            </div>
        </AuthPageWrapper>
    );
};

export default LoginPage;
